import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { ShoppingBag, User, Heart, LogOut, Calendar, Shield, Zap, Dice5, Smartphone, Gamepad2, Crown, Newspaper } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { InstallAppDialog } from "@/components/InstallAppDialog";
import { MobileMenu } from "@/components/MobileMenu";
import { useTier } from "@/context/TierContext";

export const Header = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const tierInfo = useTier();
  const navigate = useNavigate();
  const isSilverOrGold = tierInfo?.tier === "silver" || tierInfo?.tier === "gold";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <div className="w-14 h-14 md:w-24 md:h-24 flex items-center justify-center">
            <img
              src="/logo-pf.webp"
              alt="Power Fitness Nutrition"
              width="422"
              height="512"
              className="h-full w-auto object-contain group-hover:scale-105 transition-transform"
              data-testid="header-logo"
            />
          </div>
          <div className="hidden md:block">
            <div className="font-display font-black text-xl lg:text-2xl tracking-tight leading-none">POWER FITNESS<span className="text-[#D4AF37]"> NUTRITION</span></div>
            <div className="font-overline text-[10px] text-neutral-500 leading-none mt-1">PREMIUM SUPPLEMENT STORE</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/catalog" className="font-overline text-xs text-neutral-300 hover:text-[#D4AF37] transition-colors" data-testid="nav-catalog">Catalogo</Link>
          <Link to="/catalog" className="font-overline text-xs text-[#D4AF37] hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-shop-online"><ShoppingBag className="w-3 h-3" /> Acquista Online</Link>
          {user && <Link to="/wheel" className="font-overline text-xs text-[#D4AF37] hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-wheel"><Dice5 className="w-3 h-3" /> Ruota</Link>}
          {user && <Link to="/arcade" className="font-overline text-xs text-[#D4AF37] hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-arcade"><Gamepad2 className="w-3 h-3" /> Arcade</Link>}
          <Link to="/flyer" className="font-overline text-xs text-[#D4AF37] hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-flyer"><Newspaper className="w-3 h-3" /> Volantino</Link>
          <Link to="/blog" className="font-overline text-xs text-neutral-300 hover:text-[#D4AF37] transition-colors" data-testid="nav-blog">Consigli</Link>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-3">
          <InstallAppDialog
            trigger={
              <button
                className="inline-flex items-center gap-1.5 px-2.5 md:px-3 py-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all font-overline text-[10px]"
                data-testid="header-install-button"
                title="Installa l'app sulla home"
              >
                <Smartphone className="w-3.5 h-3.5" /> Installa app
              </button>
            }
          />
          <Link to="/cart" className="relative p-2 hover:bg-white/5 transition-colors" data-testid="cart-icon-button" aria-label={`Carrello, ${count} articoli`}>
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#D4AF37] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full" data-testid="cart-count">{count}</span>
            )}
          </Link>

          {user ? (
            <div className="hidden md:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 border border-transparent hover:border-white/10 transition-all" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 bg-[#D4AF37] flex items-center justify-center text-xs font-bold uppercase">
                    {user.name?.[0] || "U"}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#141414] border-white/10 text-white">
                <DropdownMenuLabel className="font-overline text-[10px] text-neutral-500">Account</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer hover:!bg-white/5 hover:!text-white" data-testid="menu-profile">
                  <User className="w-4 h-4 mr-2" /> Profilo & Punti
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/orders")} className="cursor-pointer hover:!bg-white/5 hover:!text-white" data-testid="menu-orders">
                  <ShoppingBag className="w-4 h-4 mr-2" /> Ordini
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/flyer")} className="cursor-pointer hover:!bg-white/5 hover:!text-white" data-testid="menu-flyer">
                  <Newspaper className="w-4 h-4 mr-2 text-[#D4AF37]" /> Volantino
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/reservations")} className="cursor-pointer hover:!bg-white/5 hover:!text-white" data-testid="menu-reservations">
                  <Calendar className="w-4 h-4 mr-2" /> Prenotazioni
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/wheel")} className="cursor-pointer hover:!bg-white/5 hover:!text-white" data-testid="menu-wheel">
                  <Dice5 className="w-4 h-4 mr-2 text-[#D4AF37]" /> Ruota della Fortuna
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/arcade")} className="cursor-pointer hover:!bg-white/5 hover:!text-white" data-testid="menu-arcade">
                  <Gamepad2 className="w-4 h-4 mr-2 text-[#D4AF37]" /> Arcade
                </DropdownMenuItem>
                {isSilverOrGold && (
                  <DropdownMenuItem onClick={() => navigate("/personal-shopper")} className="cursor-pointer hover:!bg-white/5 hover:!text-white" data-testid="menu-personal-shopper">
                    <Crown className="w-4 h-4 mr-2 text-[#D4AF37]" /> Personal Shopper
                  </DropdownMenuItem>
                )}
                {(user.role === "admin" || user.role === "staff") && (
                  <>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer hover:!bg-white/5 hover:!text-white" data-testid="menu-admin">
                      <Shield className="w-4 h-4 mr-2 text-[#D4AF37]" /> Pannello Admin
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer hover:!bg-white/5 hover:!text-white" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          ) : (
            <Button asChild className="hidden md:inline-flex bg-[#D4AF37] hover:bg-[#E5C158] text-white rounded-none font-overline text-xs px-5" data-testid="login-button">
              <Link to="/login">Accedi</Link>
            </Button>
          )}
          <MobileMenu user={user} isSilverOrGold={isSilverOrGold} onLogout={handleLogout} />
        </div>
      </div>
    </header>
  );
};
